import { useEffect, useState } from 'react';
import { Table, Button, Form, Row, Col, Spinner, Alert, Badge } from 'react-bootstrap';
import { apiGet, apiPost, apiDelete } from '../services/apiClient';

const presets = [
  { title: 'Terms & Conditions', slug: 'terms' },
  { title: 'Privacy Policy', slug: 'privacy' },
  { title: 'About Us', slug: 'about' },
  { title: 'Contact Us', slug: 'contact' },
];

const ContentPagesPage = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', slug: '', body: '', status: 'draft' });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiGet('/api/admin/content/pages', token);
      setItems(res.data || res);
    } catch (err) {
      setError('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setLoading(true);
    setError('');
    try {
      const url = editing ? `/api/admin/content/pages/${editing.id}` : '/api/admin/content/pages';
      await apiPost(url, form, token);
      setEditing(null);
      setForm({ title: '', slug: '', body: '', status: 'draft' });
      await load();
    } catch (err) {
      setError('Save failed');
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete page?')) return;
    try {
      await apiDelete(`/api/admin/content/static_pages/${id}`, token);
      await load();
    } catch (err) {
      setError('Delete failed');
    }
  };

  return (
    <div className="p-4 bg-white rounded shadow-sm">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0">Static pages</h4>
          <small className="text-muted">Terms, Privacy, About, Contact, etc.</small>
        </div>
        <div className="d-flex gap-2">
          {presets.map((p) => (
            <Button key={p.slug} variant="outline-secondary" size="sm" onClick={() => setForm((f) => ({ ...f, ...p }))}>
              {p.title}
            </Button>
          ))}
        </div>
      </div>
      {error && <Alert variant="danger">{error}</Alert>}
      <Form className="mb-3" onSubmit={(e) => e.preventDefault()}>
        <Row className="g-2">
          <Col md={3}>
            <Form.Control placeholder="Title" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
          </Col>
          <Col md={3}>
            <Form.Control placeholder="Slug" value={form.slug} onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))} />
          </Col>
          <Col md={4}>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Body"
              value={form.body}
              onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
            />
          </Col>
          <Col md={1}>
            <Form.Select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </Form.Select>
          </Col>
          <Col md={1}>
            <Button onClick={save} disabled={loading}>
              {editing ? 'Update' : 'Add'}
            </Button>
          </Col>
        </Row>
      </Form>
      {loading && (
        <div className="py-3 text-center">
          <Spinner animation="border" />
        </div>
      )}
      <Table hover responsive bordered size="sm">
        <thead className="table-light">
          <tr>
            <th>Title</th>
            <th>Slug</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((b) => (
            <tr key={b.id}>
              <td>{b.title}</td>
              <td>{b.slug}</td>
              <td>
                <Badge bg={b.status === 'published' ? 'success' : 'secondary'}>{b.status}</Badge>
              </td>
              <td className="text-nowrap">
                <Button size="sm" variant="outline-primary" onClick={() => { setEditing(b); setForm({ ...b }); }}>
                  Edit
                </Button>{' '}
                <Button size="sm" variant="outline-danger" onClick={() => remove(b.id)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan="4" className="text-center text-muted">
                No pages yet.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default ContentPagesPage;
